import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { useMaterialUIController } from "context";

import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { deleteAiPlan, acceptAiPlan, createAiPlanTask } from "@shared/api/aiPlans";
import { getUserGoalByPlanId } from "@shared/api/aiCoaching";
import { truncateWithEllipsis } from "utils/stringUtils";

// Declaring props types for BookingCard
interface Props {
  id: number;
  image: string;
  title: string;
  description: string;
  category: string;
  status: string;
  is_accepted: boolean;
  onDeleted?: () => void;
  [key: string]: any;
  isMobile?: boolean;
}

function PlanCard({
  id,
  image,
  title,
  description,
  category,
  status,
  is_accepted,
  onDeleted,
  isMobile,
}: Props & { onDeleted?: () => void }): JSX.Element {
  console.log(`PlanCard id:${id}`);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useContext(LanguageContext) as any;
  const { session } = useSessionContext();
  const { showNotification } = useNotification();

  const [open, setOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const navigate = useNavigate();

  const handleEdit = async () => {
    try {
      const goalData = await getUserGoalByPlanId(session.access_token, id);

      navigate("/pages/ai-coaching/GoalSetting", { state: { goalData } });
    } catch (err) {
      console.error("user_goals取得失敗:", err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.get-error"),
        },
        3000
      );
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteAiPlan(id, session.access_token);
      setOpen(false);
      onDeleted?.();
    } catch (err) {
      console.error("削除失敗:", err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("PlanCard.deleteError"),
        },
        3000
      );
    }
  };

  const handleAccept = async () => {
    try {
      await acceptAiPlan(id, session.access_token);
      showNotification(
        {
          icon: "info",
          title: t("Common.success"),
          message: t("PlanCard.acceptSuccess"),
        },
        3000
      );
      onDeleted?.();
    } catch (err) {
      console.error("許可更新失敗:", err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("PlanCard.acceptError"),
        },
        3000
      );
    }
  };

  const handleCreateTask = async () => {
    try {
      await createAiPlanTask(id, session.access_token);
      showNotification(
        {
          icon: "info",
          title: t("Common.success"),
          message: t("PlanCard.createTaskSuccess"),
        },
        3000
      );
      onDeleted?.();
    } catch (err) {
      console.error("タスク作成失敗:", err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("PlanCard.createTaskError"),
        },
        3000
      );
    }
  };

  return (
    <Card
      sx={{
        "&:hover .card-header": {
          transform: "translate3d(0, -50px, 0)",
        },
      }}
    >
      <MDBox
        position="relative"
        borderRadius="lg"
        mt={-3}
        mx={2}
        className="card-header"
        sx={{ transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)" }}
      >
        <MDBox
          component="img"
          src={image}
          alt={title}
          borderRadius="lg"
          shadow="md"
          width="100%"
          height="100%"
          position="relative"
          zIndex={1}
        />
        <MDBox
          borderRadius="lg"
          shadow="md"
          width="100%"
          height="100%"
          position="absolute"
          left={0}
          top="0"
          sx={{
            backgroundImage: `url(${image})`,
            transform: "scale(0.94)",
            filter: "blur(12px)",
            backgroundSize: "cover",
          }}
        />
      </MDBox>
      <MDBox textAlign="center" pt={3} px={3}>
        <MDBox display="flex" justifyContent="center" alignItems="center" mt={-8}>
          <MDBox mr={1}>
            <MDButton variant="text" color="error" onClick={() => setOpen(true)}>
              <Icon>delete</Icon>&nbsp;{t("PlanCard.delete")}
            </MDButton>
          </MDBox>
        </MDBox>

        {/* ダイアログの実装 */}
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>{t("PlanCard.delete")}</DialogTitle>
          <DialogContent>{t("PlanCard.confirmDelete")}</DialogContent>
          <DialogActions>
            <MDButton onClick={() => setOpen(false)}>{t("PlanCard.cancel")}</MDButton>
            <MDButton onClick={handleDeleteConfirm} color="error">
              {t("PlanCard.ok")}
            </MDButton>
          </DialogActions>
        </Dialog>

        <MDTypography variant="h5" fontWeight="regular" sx={{ mt: 4 }}>
          {title}
        </MDTypography>
        
        {/* カード上の省略表示部分 */}
        <MDTypography variant="body2" color="text" sx={{ mt: 1.5, mb: 1, lineHeight: 1.6 }}>
          {truncateWithEllipsis(description, 150)
            .replace(/。/g, "。\n") // 「。」の後に改行を挿入
            .split("\n")
            .filter((line) => line.trim() !== "") // 空行を除外
            .map((line, index) => (
              <span key={index} style={{ display: "block", marginBottom: "8px" }}>
                {line}
              </span>
            ))}
        </MDTypography>
      </MDBox>

      <Divider />
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={0.5}
        pb={3}
        px={3}
        lineHeight={1}
      >
        {/* 詳細ダイアログ */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          fullWidth
          maxWidth="xl"
          PaperProps={{
            sx: {
              height: "500px",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <DialogTitle>{title}</DialogTitle>
          <DialogContent
            dividers
            sx={{
              overflowY: "auto",
              flex: 1,
              p: 0,
            }}
          >
            <Card
              sx={{
                m: 2,
                p: 2,
                height: "100%",
                overflowY: "auto",
              }}
            >
              {/* ダイアログ内の詳細表示部分: 若干大きくし、色を黒に変更 */}
              <MDTypography 
                variant="body2" 
                color="dark"
                sx={{ 
                  lineHeight: 1.8,
                  letterSpacing: '0.02em',
                  fontSize: '0.9rem', // 0.85remから0.9remへ少し大きく
                  color: '#000000', // 色を黒に指定
                }}
              >
                {description
                  .replace(/。/g, "。\n") // 「。」の後に改行を挿入
                  .split("\n")
                  .filter((line) => line.trim() !== "") // 空行を除外
                  .map((line, index) => (
                    <span 
                      key={index} 
                      style={{ 
                        display: "block", 
                        marginBottom: "16px" 
                      }}
                    >
                      {line}
                    </span>
                  ))}
              </MDTypography>
            </Card>
          </DialogContent>
          <DialogActions>
            <MDButton color="dark" variant="gradient" onClick={() => setDetailDialogOpen(false)}>
              {t("PlanCard.close")}
            </MDButton>
          </DialogActions>
        </Dialog>
        <Grid container spacing={isMobile ? 1 : 2} sx={{ width: "100%" }}>
          {isMobile ? (
            <>
              {/* モバイル表示: 詳細と許可を1行目に、編集とタスク作成を2行目に */}
              <Grid item xs={6}>
                <MDButton
                  color="light"
                  variant="gradient"
                  fullWidth
                  onClick={() => setDetailDialogOpen(true)}
                >
                  {t("PlanCard.detail")}
                </MDButton>
              </Grid>
              <Grid item xs={6}>
                <MDButton
                  color="info"
                  variant="gradient"
                  fullWidth
                  onClick={handleAccept}
                  disabled={is_accepted}
                >
                  {t("PlanCard.permission")}
                </MDButton>
              </Grid>
              {/* 編集とタスク作成ボタンを次の行に配置 */}
              <Grid item xs={6}>
                <MDButton color="light" variant="gradient" fullWidth onClick={handleEdit}>
                  {t("PlanCard.edit")}
                </MDButton>
              </Grid>
              <Grid item xs={6}>
                <MDButton
                  color="success"
                  variant="gradient"
                  fullWidth
                  onClick={handleCreateTask}
                  disabled={!is_accepted || status === "4"}
                >
                  {t("PlanCard.createTask")}
                </MDButton>
              </Grid>
            </>
          ) : (
            <>
              {/* デスクトップ表示: 既存のレイアウトに近い形で */}
              <Grid item xs={12} sm={8}>
                <MDBox display="flex" gap={1}>
                  <MDButton
                    color="light"
                    variant="gradient"
                    onClick={() => setDetailDialogOpen(true)}
                  >
                    {t("PlanCard.detail")}
                  </MDButton>
                  <MDButton color="light" variant="gradient" onClick={handleEdit}>
                    {t("PlanCard.edit")}
                  </MDButton>
                  <MDButton
                    color="info"
                    variant="gradient"
                    onClick={handleAccept}
                    disabled={is_accepted}
                  >
                    {t("PlanCard.permission")}
                  </MDButton>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDBox display="flex" justifyContent="flex-end">
                  <MDButton
                    color="success"
                    variant="gradient"
                    onClick={handleCreateTask}
                    disabled={!is_accepted || status === "4"}
                  >
                    {t("PlanCard.createTask")}
                  </MDButton>
                </MDBox>
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>
    </Card>
  );
}

// Declaring default props for PlanCard
PlanCard.defaultProps = {
  action: false,
  isMobile: false,
};

export default PlanCard;